/**
 * Which page Home's content area shows.
 *
 * Home used to decide this with a chain of inline boolean expressions spread
 * through the render tree. Two of them were independently inverted at
 * different times, and both inversions presented the same way to the user: a
 * completely blank taskpane, because every branch evaluated false and nothing
 * was drawn. So the decision is extracted here, is pure, and is pinned by a
 * test matrix.
 *
 * Order matters and mirrors the previous render order:
 *   settings -> adoption -> auth -> organization picker ->
 *   main / loading / error -> select
 */

export interface HomePageState {
  signedIn: boolean;
  showSettings: boolean;
  /** Reached by the header Back button, not by the first-time sign-in. */
  showOrganizationPicker: boolean;
  /** True only once a project AND template are resolved for the document. */
  displayMainFunc: boolean;
  adoptionPageActive: boolean;
  /** The document carries a jenlyReportDownloadId written by the workspace. */
  isDownloadedDocument: boolean;
  /** The data MainPage dereferences during render has arrived. */
  mainDataReady: boolean;
  /**
   * Loading that data failed, so offer a retry rather than an endless spinner.
   * Also covers a downloaded document whose context could not be resolved at
   * all (unsupported schema version, failed fetch, missing ids).
   */
  hasMainDataError: boolean;
}

export type HomePage =
  /** Microsoft sign-in, including the first-time organization picker. */
  | "auth"
  /** Organization picker reached by going Back from a signed-in page. */
  | "organizationPicker"
  | "settings"
  /** Downloaded-report adoption pass, between auth and MainPage. */
  | "adoption"
  /** Downloaded document whose project/template data is still arriving. */
  | "loading"
  /** Same, but the load failed. */
  | "error"
  | "main"
  /** No project/template in the document: user is starting a new report run. */
  | "select";

export const resolveHomePage = ({
  signedIn,
  showSettings,
  showOrganizationPicker,
  displayMainFunc,
  adoptionPageActive,
  isDownloadedDocument,
  mainDataReady,
  hasMainDataError,
}: HomePageState): HomePage => {
  if (showSettings) return "settings";
  // Rendered independently of auth state: during adoption the user has
  // authenticated but signedIn is deliberately not flipped yet.
  if (adoptionPageActive) return "adoption";
  if (!signedIn) return "auth";
  if (showOrganizationPicker) return "organizationPicker";
  if (displayMainFunc) {
    if (mainDataReady) return "main";
    return hasMainDataError ? "error" : "loading";
  }
  // No project/template resolved. A normal document starts a new report run
  // here, so offer the picker.
  //
  // A downloaded document in this state has not resolved its context — or
  // failed to: an unsupported jenlyDownloadSchemaVersion, a failed
  // /addin-context fetch, or missing project/template ids all leave
  // displayMainFunc false while the document is still bound to a project. The
  // picker must NOT be offered there, because it would let the user contradict
  // the document they opened. Reuse the waiting/retry surface instead, so the
  // pane is never empty.
  if (isDownloadedDocument) return hasMainDataError ? "error" : "loading";
  return "select";
};